import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { fetchCheckoutCredited } from '../services/api';
import {
  BUNDLES,
  bundleCreditedMessage,
  creditCountLabel,
  getBundleById,
  isSubscriptionPaymentUnconfirmed,
  type BundleId,
} from '../../shared/types';
import type { PromoNotice } from './usePromoRedemption';

/**
 * Message affiché au retour de Stripe Checkout.
 *
 * Les URL de retour (server/routes/stripe.ts) :
 *   - pack       : `/?purchase=success&bundle=<id>&session_id=<cs_…>`
 *   - abonnement : `/billing?success=true&session_id=<cs_…>`
 *   - abandon    : `/pricing?canceled=true`
 *
 * Le webhook qui crédite peut arriver quelques secondes APRÈS la redirection :
 * on interroge donc le serveur toutes les 2 s pendant 20 s au plus, et le
 * message n'annonce les crédits (ou le plan) qu'une fois qu'ils sont là. Passé
 * ce délai, il dit qu'ils arriveront d'ici quelques minutes.
 *
 * Les paramètres sont lus et retirés de l'URL UNE SEULE FOIS, au chargement du
 * module (même raison que readRecoveryToken dans AuthContext : sous StrictMode
 * un initialiseur de useState est rejoué). Un rechargement de la page ne
 * réaffiche donc pas le message.
 */

type PurchaseReturn =
  | { kind: 'bundle'; bundleId: BundleId; sessionId: string | null }
  | { kind: 'subscription' }
  | { kind: 'canceled' };

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 20000;
const SUPPORT_EMAIL = 'contact@loievin.fr';

const PLAN_NAMES: Record<string, string> = { pro: 'Pro', premium: 'Premium' };

function readPurchaseReturn(): PurchaseReturn | null {
  if (typeof window === 'undefined') return null;
  try {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    let result: PurchaseReturn | null = null;
    let consumed: string[] = [];

    if (params.get('purchase') === 'success') {
      const bundleId = params.get('bundle');
      const sessionId = params.get('session_id');
      if (bundleId && BUNDLES.some((b) => b.id === bundleId)) {
        result = {
          kind: 'bundle',
          bundleId: bundleId as BundleId,
          sessionId: sessionId && sessionId.startsWith('cs_') ? sessionId : null,
        };
      }
      consumed = ['purchase', 'bundle', 'session_id'];
    } else if (url.pathname.startsWith('/billing') && params.get('success') === 'true') {
      result = { kind: 'subscription' };
      consumed = ['success', 'session_id'];
    } else if (params.get('canceled') === 'true') {
      result = { kind: 'canceled' };
      consumed = ['canceled'];
    }

    if (consumed.length === 0) return null;
    for (const key of consumed) params.delete(key);
    // /billing et /pricing ne sont pas des routes du client (navigation par
    // état) : on revient à la racine.
    const path = url.pathname.startsWith('/billing') || url.pathname.startsWith('/pricing') ? '/' : url.pathname;
    const search = params.toString();
    window.history.replaceState({}, '', `${path}${search ? `?${search}` : ''}${url.hash}`);
    return result;
  } catch {
    return null;
  }
}

const INITIAL_PURCHASE_RETURN = readPurchaseReturn();

type Phase = 'waiting' | 'confirmed' | 'delayed';

export function usePurchaseReturn(): { notice: PromoNotice | null; dismiss: () => void } {
  const { user, organization, refreshProfile } = useAuth();
  const [purchase] = useState<PurchaseReturn | null>(INITIAL_PURCHASE_RETURN);
  const [phase, setPhase] = useState<Phase>('waiting');
  const [dismissed, setDismissed] = useState(false);
  const startedRef = useRef(false);
  // Solde de départ : sert seulement au repli sans session_id (lien de retour
  // d'avant cette version), où l'on guette une hausse du solde affiché.
  const baselineCreditsRef = useRef<number | null>(null);

  const loggedIn = !!user;

  // ── Interrogation périodique ────────────────────────────────────────
  useEffect(() => {
    if (!purchase || purchase.kind === 'canceled' || !loggedIn || startedRef.current) return;
    startedRef.current = true;

    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const deadline = Date.now() + POLL_TIMEOUT_MS;

    const tick = async () => {
      if (stopped) return;
      try {
        if (purchase.kind === 'bundle' && purchase.sessionId) {
          const credited = await fetchCheckoutCredited(purchase.sessionId);
          if (stopped) return;
          if (credited) {
            // Recharge le solde affiché AVANT d'annoncer les crédits.
            await refreshProfile();
            if (!stopped) setPhase('confirmed');
            return;
          }
        } else {
          // Abonnement (ou pack sans session_id) : on recharge l'org, et
          // l'effet ci-dessous constate le plan ou la hausse du solde.
          await refreshProfile();
        }
      } catch {
        // Erreur réseau passagère : on retente au prochain tour.
      }
      if (stopped) return;
      if (Date.now() >= deadline) {
        // Dernier rechargement : le solde affiché reste au plus juste.
        void refreshProfile();
        setPhase((p) => (p === 'waiting' ? 'delayed' : p));
        return;
      }
      timer = setTimeout(() => void tick(), POLL_INTERVAL_MS);
    };

    void tick();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      // Démontage (StrictMode compris) : on autorise un redémarrage propre.
      startedRef.current = false;
    };
    // refreshProfile est stable (useCallback) ; on ne relance pas sur ses changements.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchase, loggedIn]);

  // ── Constat à partir des données de l'org ──────────────────────────
  const plan = organization?.plan;
  const subscriptionStatus = organization?.subscriptionStatus ?? null;
  const creditBalance = organization?.creditBalance;
  useEffect(() => {
    if (!purchase || phase === 'confirmed') return;
    if (purchase.kind === 'subscription') {
      if ((plan === 'pro' || plan === 'premium') && !isSubscriptionPaymentUnconfirmed(subscriptionStatus)) {
        setPhase('confirmed');
      }
      return;
    }
    if (purchase.kind === 'bundle' && !purchase.sessionId && typeof creditBalance === 'number') {
      if (baselineCreditsRef.current === null) {
        baselineCreditsRef.current = creditBalance;
      } else if (creditBalance > baselineCreditsRef.current) {
        setPhase('confirmed');
      }
    }
  }, [purchase, phase, plan, subscriptionStatus, creditBalance]);

  if (!purchase || dismissed) return { notice: null, dismiss: () => setDismissed(true) };

  return { notice: buildNotice(purchase, phase, plan), dismiss: () => setDismissed(true) };
}

function buildNotice(purchase: PurchaseReturn, phase: Phase, plan: string | undefined): PromoNotice {
  if (purchase.kind === 'canceled') {
    return {
      kind: 'info',
      title: "Paiement annulé, rien n'a été débité.",
      message: '',
    };
  }

  if (purchase.kind === 'bundle') {
    const bundle = getBundleById(purchase.bundleId)!;
    const credits = creditCountLabel(bundle.totalCredits);
    if (phase === 'confirmed') {
      return {
        kind: 'success',
        title: `Paiement confirmé : ${bundle.name}`,
        message: bundleCreditedMessage(bundle),
      };
    }
    if (phase === 'delayed') {
      return {
        kind: 'info',
        title: `Paiement reçu : ${bundle.name}`,
        message: `Vos ${credits} apparaîtront sur votre compte d'ici quelques minutes. S'ils ne sont toujours pas visibles, écrivez-nous à ${SUPPORT_EMAIL}.`,
      };
    }
    return {
      kind: 'info',
      title: `Paiement reçu : ${bundle.name}`,
      message: `Ajout de vos ${credits} en cours, cela prend quelques secondes.`,
    };
  }

  // Abonnement
  if (phase === 'confirmed') {
    // Phase atteinte seulement quand l'org est en pro ou premium.
    const planName = PLAN_NAMES[plan ?? ''] ?? 'Pro';
    return {
      kind: 'success',
      title: `Paiement confirmé : abonnement ${planName}`,
      message: `Votre abonnement ${planName} est actif sur votre compte.`,
    };
  }
  if (phase === 'delayed') {
    return {
      kind: 'info',
      title: 'Paiement reçu',
      message: `Votre abonnement sera actif d'ici quelques minutes. S'il ne l'est toujours pas, écrivez-nous à ${SUPPORT_EMAIL}.`,
    };
  }
  return {
    kind: 'info',
    title: 'Paiement reçu',
    message: 'Activation de votre abonnement en cours, cela prend quelques secondes.',
  };
}
